import { useEffect, useMemo, useRef, useState } from 'react';

type Grid = number[][];
type Quad = 0 | 1 | 2 | 3;
type History = { items: Grid[]; index: number };

const make16 = (): Grid => Array.from({ length: 16 }, () => Array(16).fill(0));
const clone16 = (g: Grid): Grid => g.map(r => r.slice());
const quadOffset = (q: Quad) => ({ ox: (q % 2) * 8, oy: (q >= 2 ? 1 : 0) * 8 });

const hasColorInRect = (g: Grid, x0: number, y0: number, w: number, h: number) => {
  for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) if ((g[y]?.[x] ?? 0) !== 0) return true;
  return false;
};
const sliceQuad = (g: Grid, q: Quad) => {
  const { ox, oy } = quadOffset(q);
  const out = Array.from({ length: 8 }, () => Array(8).fill(0));
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) out[y][x] = g[oy + y][ox + x];
  return out;
};
const countUsedQuads = (g: Grid) => {
  let c = 0;
  if (hasColorInRect(g, 0, 0, 8, 8)) c++;
  if (hasColorInRect(g, 8, 0, 8, 8)) c++;
  if (hasColorInRect(g, 0, 8, 8, 8)) c++;
  if (hasColorInRect(g, 8, 8, 8, 8)) c++;
  return c;
};

// NYTT: sammanfattning av kvadrantanvändning
const computeUsage = (g: Grid) => {
  const mask =
    (hasColorInRect(g, 0, 0, 8, 8) ? 1 : 0) |
    (hasColorInRect(g, 8, 0, 8, 8) ? 2 : 0) |
    (hasColorInRect(g, 0, 8, 8, 8) ? 4 : 0) |
    (hasColorInRect(g, 8, 8, 8, 8) ? 8 : 0);
  const count = ((mask & 1) ? 1 : 0) + ((mask & 2) ? 1 : 0) + ((mask & 4) ? 1 : 0) + ((mask & 8) ? 1 : 0);
  const first: Quad = (mask & 1) ? 0 : (mask & 2) ? 1 : (mask & 4) ? 2 : (mask & 8) ? 3 : 0;
  return { mask, count, first };
};

// Lightweight hash for a 16×16 grid (dirty-check)
const hashGrid = (g: Grid) => g.map(r => r.join(',')).join('|');

// NEW: draft persistence (localStorage)
const DRAFT_KEY = 'editor:draft:v1';
type DraftV1 = {
  grid: Grid;
  gridSize: 8 | 16;
  activeQuad: Quad;
  updatedAt: number;
};
const readDraft = (): DraftV1 | null => {
  try {
    const s = localStorage.getItem(DRAFT_KEY);
    if (!s) return null;
    const d = JSON.parse(s) as DraftV1;
    // basic validation
    if (!Array.isArray(d?.grid) || d.grid.length !== 16 || !Array.isArray(d.grid[0]) || d.grid[0].length !== 16) {
      return null;
    }
    return d;
  } catch {
    return null;
  }
};
const writeDraft = (draft: DraftV1) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota/availability errors
  }
};

export function useEditorStack(getSelectedColor: () => number) {
  // Historik
  const [hist, setHist] = useState<History>({ items: [make16()], index: 0 });
  const present = hist.items[hist.index];

  // Saved/dirty
  const [savedHash, setSavedHash] = useState<string>(() => hashGrid(make16()));
  const currentHash = useMemo(() => hashGrid(present), [present]);
  const isDirty = currentHash !== savedHash;
  const markSaved = () => setSavedHash(currentHash);

  // Stroke
  const strokeActiveRef = useRef(false);
  const strokeCommittedRef = useRef(false);

  // Grid-läge
  const [gridSize, setGridSize] = useState<8 | 16>(8);
  const [activeQuad, setActiveQuad] = useState<Quad>(0);
  const [isQuadSelectMode, setIsQuadSelectMode] = useState(false);
  const gridSizeRef = useRef(gridSize);
  useEffect(() => { gridSizeRef.current = gridSize; }, [gridSize]);

  // Debug
  const debugOnRef = useRef<boolean>((() => {
    try { return (localStorage.getItem('editor:debug') ?? '0') === '1'; } catch { return false; }
  })());
  const log = (...args: any[]) => { if (debugOnRef.current) console.log('[EDITOR]', ...args); };

  // Render-pixlar
  const pixels: Grid =
    gridSize === 16
      ? present
      : (() => {
          const { ox, oy } = quadOffset(activeQuad);
          const out = Array.from({ length: 8 }, () => Array(8).fill(0));
          for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) out[y][x] = present[oy + y][ox + x];
          return out;
        })();

  // Mobil-preview (visas i 8×8 när 2+ kvadranter används, hålls 2s)
  const [mobilePreviewVisible, setMobilePreviewVisible] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  useEffect(() => () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); }, []);
  const pokeMobilePreview = (grid: Grid) => {
    if (gridSizeRef.current !== 8) return;
    if (countUsedQuads(grid) < 2) return;
    setMobilePreviewVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      setMobilePreviewVisible(false);
      hideTimerRef.current = null;
    }, 2000);
  };

  // Storlek/kvadrant
  const beginQuadSelect = () => { setIsQuadSelectMode(true); setGridSize(16); };
  const chooseQuadrant = (q: Quad) => { setActiveQuad(q); setIsQuadSelectMode(false); setGridSize(8); };
  const toggleGridSize = () => { setIsQuadSelectMode(false); gridSize === 16 ? beginQuadSelect() : setGridSize(16); };

  // Undo/Redo
  const doUndo = () => {
    setHist(h => {
      if (h.index === 0) { log('UNDO_NOP'); return h; }
      const next = { items: h.items, index: h.index - 1 };
      pokeMobilePreview(next.items[next.index]);
      return next;
    });
  };
  const doRedo = () => {
    setHist(h => {
      if (h.index >= h.items.length - 1) { log('REDO_NOP'); return h; }
      const next = { items: h.items, index: h.index + 1 };
      pokeMobilePreview(next.items[next.index]);
      return next;
    });
  };
  const canUndo = hist.index > 0;
  const canRedo = hist.index < hist.items.length - 1;

  // Stroke
  const onStrokeStart = () => { strokeActiveRef.current = true; strokeCommittedRef.current = false; };
  const onStrokeEnd = () => { strokeActiveRef.current = false; strokeCommittedRef.current = false; pokeMobilePreview(present); };

  // Historik-mutationer
  const pushSnapshot = (nextPresent: Grid) => {
    setHist(h => {
      const base = h.items.slice(0, h.index + 1);
      return { items: [...base, nextPresent], index: base.length };
    });
    pokeMobilePreview(nextPresent);
  };
  const replaceCurrent = (nextPresent: Grid) => {
    setHist(h => {
      const items = h.items.slice();
      items[h.index] = nextPresent;
      return { items, index: h.index };
    });
    pokeMobilePreview(nextPresent);
  };

  // Målning
  const applyPixel16 = (ax: number, ay: number, color: number, reason: 'stroke' | 'tap') => {
    if (ax < 0 || ay < 0 || ax >= 16 || ay >= 16) return;
    const curr = present[ay][ax];
    if (curr === color) return;

    const next = clone16(present);
    next[ay][ax] = color;

    if (reason === 'stroke') {
      if (!strokeCommittedRef.current) { strokeCommittedRef.current = true; pushSnapshot(next); }
      else { replaceCurrent(next); }
    } else {
      pushSnapshot(next);
    }
  };

  const handlePixelClick = (x: number, y: number) => {
    const color = getSelectedColor();
    const { ox, oy } = gridSize === 8 ? quadOffset(activeQuad) : { ox: 0, oy: 0 };
    applyPixel16(ox + x, oy + y, color, strokeActiveRef.current ? 'stroke' : 'tap');
  };

  // Operationer
  const createEmptyCanvas = () => {
    const n = make16();
    setHist({ items: [n], index: 0 });
    setSavedHash(hashGrid(n)); // ny tom → betraktas som sparad bas
    pokeMobilePreview(n);
  };
  const clearCanvas = () => { const n = make16(); if (JSON.stringify(n) !== JSON.stringify(present)) pushSnapshot(n); };
  const rotateSprite = () => {
    const { count, first } = computeUsage(present);

    if (count >= 2) {
      // Rotera hela 16×16
      const next = make16();
      for (let i = 0; i < 16; i++) for (let j = 0; j < 16; j++) next[j][15 - i] = present[i][j];
      if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
      return;
    }

    // Rotera endast en 8×8 (den aktiva, eller den enda använda)
    const q: Quad = count === 1 ? first : activeQuad;
    const { ox, oy } = quadOffset(q);
    const next = clone16(present);
    const tmp = Array.from({ length: 8 }, () => Array(8).fill(0));
    for (let i = 0; i < 8; i++) for (let j = 0; j < 8; j++) tmp[j][7 - i] = present[oy + i][ox + j];
    for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) next[oy + y][ox + x] = tmp[y][x];
    if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
  };
  const flipSpriteHorizontal = () => {
    const { count, first } = computeUsage(present);

    if (count >= 2) {
      // Spegla hela 16×16
      const next = clone16(present);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 8; x++) {
        const a = x, b = 15 - x;
        const t = next[y][a]; next[y][a] = next[y][b]; next[y][b] = t;
      }
      if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
      return;
    }

    // Spegla endast en 8×8 (den aktiva, eller den enda använda)
    const q: Quad = count === 1 ? first : activeQuad;
    const { ox, oy } = quadOffset(q);
    const next = clone16(present);
    for (let y = 0; y < 8; y++) for (let x = 0; x < 4; x++) {
      const a = ox + x, b = ox + (7 - x);
      const t = next[oy + y][a]; next[oy + y][a] = next[oy + y][b]; next[oy + y][b] = t;
    }
    if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
  };
  const shiftSpriteRight = () => {
    const { count, first } = computeUsage(present);

    if (count >= 2) {
      // Skjut hela 16×16 ett steg höger (wrap)
      const next = make16();
      for (let y = 0; y < 16; y++) {
        for (let x = 0; x < 16; x++) {
          const nx = (x + 1) & 15;
          next[y][nx] = present[y][x];
        }
      }
      if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
      return;
    }

    // Skjut endast aktiv/enda 8×8-kvadranten
    const q: Quad = count === 1 ? first : activeQuad;
    const { ox, oy } = quadOffset(q);
    const next = clone16(present);

    for (let y = 0; y < 8; y++) {
      const row = new Array(8);
      for (let x = 0; x < 8; x++) {
        row[(x + 1) & 7] = present[oy + y][ox + x];
      }
      for (let x = 0; x < 8; x++) next[oy + y][ox + x] = row[x];
    }

    if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
  };
  const shiftSpriteDown = () => {
    const { count, first } = computeUsage(present);

    if (count >= 2) {
      // Skjut hela 16×16 ett steg ned (wrap)
      const next = make16();
      for (let y = 0; y < 16; y++) {
        const ny = (y + 1) & 15;
        for (let x = 0; x < 16; x++) next[ny][x] = present[y][x];
      }
      if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
      return;
    }

    // Skjut endast aktiv/enda 8×8-kvadranten
    const q: Quad = count === 1 ? first : activeQuad;
    const { ox, oy } = quadOffset(q);
    const next = clone16(present);
    // bygg temp 8×8 med nedskiftning
    const tmp = Array.from({ length: 8 }, () => Array(8).fill(0));
    for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) tmp[(y + 1) & 7][x] = present[oy + y][ox + x];
    for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) next[oy + y][ox + x] = tmp[y][x];

    if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
  };
  // NYTT: skjut uppåt (wrap)
  const shiftSpriteUp = () => {
    const { count, first } = computeUsage(present);
    if (count >= 2) {
      const next = make16();
      for (let y = 0; y < 16; y++) {
        const ny = (y + 15) & 15; // y-1 mod 16
        for (let x = 0; x < 16; x++) next[ny][x] = present[y][x];
      }
      if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
      return;
    }
    const q: Quad = count === 1 ? first : activeQuad;
    const { ox, oy } = quadOffset(q);
    const next = clone16(present);
    const tmp = Array.from({ length: 8 }, () => Array(8).fill(0));
    for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) tmp[(y + 7) & 7][x] = present[oy + y][ox + x];
    for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) next[oy + y][ox + x] = tmp[y][x];
    if (JSON.stringify(next) !== JSON.stringify(present)) pushSnapshot(next);
  };

  // Ladda sprite
  const setFromSprite = (p: Grid) => {
    const h = p?.length ?? 0, w = p?.[0]?.length ?? 0;
    if (h === 16 || w === 16) {
      const next = p.map(r => r.slice());
      setHist({ items: [next], index: 0 });
      setGridSize(16); setActiveQuad(0);
      setSavedHash(hashGrid(next));        // markerad som sparad
      pokeMobilePreview(next as Grid);
    } else {
      const base = make16();
      for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) base[y][x] = p[y]?.[x] ?? 0;
      setHist({ items: [base], index: 0 });
      setGridSize(8); setActiveQuad(0);
      setSavedHash(hashGrid(base));        // markerad som sparad
      pokeMobilePreview(base);
    }
  };

  // Preview/Save
  const usageMask =
    (hasColorInRect(present, 0, 0, 8, 8) ? 1 : 0) |
    (hasColorInRect(present, 8, 0, 8, 8) ? 2 : 0) |
    (hasColorInRect(present, 0, 8, 8, 8) ? 4 : 0) |
    (hasColorInRect(present, 8, 8, 8, 8) ? 8 : 0);
  const usedCount =
    ((usageMask & 1) ? 1 : 0) + ((usageMask & 2) ? 1 : 0) + ((usageMask & 4) ? 1 : 0) + ((usageMask & 8) ? 1 : 0);
  const firstUsedQuad: Quad =
    (usageMask & 1) ? 0 : (usageMask & 2) ? 1 : (usageMask & 4) ? 2 : (usageMask & 8) ? 3 : 0;
  const previewPixels: Grid = usedCount <= 1 ? sliceQuad(present, firstUsedQuad) : present;
  const savePixels: Grid = previewPixels;

  // Restore last draft on mount
  useEffect(() => {
    const d = readDraft?.();
    if (!d) return;
    setHist({ items: [d.grid.map((r: number[]) => r.slice())], index: 0 });
    setGridSize(d.gridSize);
    setActiveQuad(d.activeQuad);
    pokeMobilePreview?.(d.grid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on changes (debounced)
  useEffect(() => {
    const id = window.setTimeout(() => {
      writeDraft?.({
        grid: present,
        gridSize,
        activeQuad,
        updatedAt: Date.now(),
      } as any);
    }, 150);
    return () => clearTimeout(id);
  }, [present, gridSize, activeQuad]);

  // NEW: Flood fill
 const fillAt = (x: number, y: number) => {
    const { ox: qx, oy: qy } = quadOffset(activeQuad);

    // Startkoordinat alltid absolut (vid 8-läge: lägg till kvadrantens offset)
    const sx = gridSize === 8 ? qx + x : x;
    const sy = gridSize === 8 ? qy + y : y;

    if (present[sy]?.[sx] === undefined) return;
    const target = present[sy][sx];
    const newColor = getSelectedColor();
    if (target === newColor) return;

    // Bestäm fyllområde:
    // - Om gridSize=16 → alltid hela 16×16
    // - Om gridSize=8 men >1 kvadrant används → hela 16×16
    // - Annars → aktiv 8×8-kvadrant
    const used = countUsedQuads(present);
    const full = gridSize === 16 || used > 1;

    const bx = full ? 0 : qx;
    const by = full ? 0 : qy;
    const xMin = bx, yMin = by, xMax = bx + (full ? 16 : 8) - 1, yMax = by + (full ? 16 : 8) - 1;

    // Flood fill (4-neighbours)
    const next = present.map(r => r.slice());
    const stack: Array<[number, number]> = [[sx, sy]];
    let changed = false;

    while (stack.length) {
      const [cx, cy] = stack.pop()!;
      if (cx < xMin || cx > xMax || cy < yMin || cy > yMax) continue;
      if (next[cy][cx] !== target) continue;
      next[cy][cx] = newColor;
      changed = true;
      stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
    }

    if (changed) pushSnapshot(next); // ett undo-steg
  };

  return {
    // view
    pixels,
    gridSize,
    activeQuad,
    isQuadSelectMode,
    previewPixels,
    savePixels,

    // mobil overlay
    overlayPixels: present,
    mobilePreviewVisible,

    // dirty/saved
    isDirty,
    markSaved,

    // painting
    handlePixelClick,
    onStrokeStart,
    onStrokeEnd,

    // ops
    createEmptyCanvas,
    clearCanvas,
    rotateSprite,
    flipSpriteHorizontal,
    shiftSpriteRight,
    shiftSpriteDown,
    shiftSpriteUp, // NEW

    // history
    doUndo,
    doRedo,
    canUndo,
    canRedo,

    // size/quad
    setGridSize,
    setActiveQuad,
    setFromSprite,
    toggleGridSize,
    beginQuadSelect,
    chooseQuadrant,

    // NEW: flood fill
    fillAt,
  };
}